/* eslint-disable no-param-reassign */
import type { FeatureCollection, Position } from "geojson";
import { Observable, Subject, Subscription, BehaviorSubject } from "rxjs";
import mapboxgl, { Map, StyleSpecification, VectorSourceSpecification } from "@iventis/mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import DrawRectangle from "mapbox-gl-draw-rectangle-mode";
import { CadUploadConfigUnit } from "@iventis/domain-model/model/cadUploadConfigUnit";
import { Asset } from "@iventis/domain-model/model/asset";
import axios from "axios";
import { CadApiConfig, PutCadApiConfig } from "../types/cad-api-config";
import { cadFillLayer, cadLineLayer, cadSourceId, cadTextLayer } from "./mapbox-georeferencer-layers";
import type { SubjectContext } from "../state/subjects-context";
import { mapboxKey } from "../api-keys";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import { GeoReferencerCadMode } from "../types/georeferencer-cad-mode";
import { CornerPosition } from "../geo-helpers/get-feature-collection-corners";
import { blankMapBackgrounds, MapBackground } from "../types/map-background";
import { mapRequestInterceptor } from "./mapbox-cad-editor-helpers";
import { ICadApiContext } from "../context/cad-api-context";

mapboxgl.accessToken = mapboxKey;

export class MapboxCadGeoReferencer {
    /** Mapbox instance the cad geojson is shown on */
    private readonly cadMap: Map;

    /** Mapbox instance which is the real world map */
    private readonly backgroundMap: Map;

    private readonly cadMapDraw: MapboxDraw;

    private readonly backgroundMapDraw: MapboxDraw;

    /** If background map is allowed to move */
    private allowBackgroundMove = true;

    private googleMapsUrl: string;

    private readonly osMapsUrl: string;

    private subscriptions: Subscription[] = [];

    private readonly zoom$: SubjectContext["mapZoom$"];

    private mode: GeoReferencerCadMode;

    private displayControlPoints = true;

    private cadControlPoints: { corner: CornerPosition; cadMapWorld: Position; cad: Position }[] = [];

    public originalCadBounds: { minX: number; minY: number; maxX: number; maxY: number } | null = null;

    private projectedCenter: [number, number] = null;

    public configRotation = 0;

    public units: CadUploadConfigUnit | null = null;

    public onControlPointsChange = new Subject<{ corner: CornerPosition; realWorld: number[]; cad: number[] }[]>();

    private onLoaded: () => void;

    private cadMapReady = false;

    private backgroundMapReady = false;

    private readonly getGoogleMapBackgroundApiKey: () => Promise<{ apiKey: string; session: string }>;

    public projecting$ = new BehaviorSubject<boolean>(false);

    public projectionPercentage$ = new BehaviorSubject<number>(0);

    private projectionAbortController: AbortController | null = null;

    private cadApiContext: ICadApiContext;

    private getSitemapVersionLevelAsset?: (levelId: string) => Promise<Asset>;

    private existingSitemapVersionLevelOverlay: { id: string; style: StyleSpecification; sourceId: string; tileUrl: string } = null;

    constructor(
        onLoaded: () => void,
        cadMapElement: HTMLDivElement,
        backgroundMapElement: HTMLDivElement,
        mapApiKeys: { getGoogleMapBackgroundApiKey: () => Promise<{ apiKey: string; session: string }>; osMapsApiKey: string },
        subjects: SubjectContext,
        cadApiContext: ICadApiContext,
        getSitemapVersionLevelAsset?: (levelId: string) => Promise<Asset>
    ) {
        this.onLoaded = onLoaded;
        this.cadMapDraw = this.createCadMapDraw();
        this.backgroundMapDraw = this.createBackgroundMapDraw();
        this.cadMap = this.createCadMap(cadMapElement);
        this.backgroundMap = this.createMapbackgroundMap(backgroundMapElement);
        this.zoom$ = subjects.mapZoom$;
        this.mode = subjects.mode$.value;
        this.subscribeToZoomEvents(subjects.mapZoom$);
        this.cadApiContext = cadApiContext;

        this.syncMouseEventsBetweenMaps();
        this.disableCadMapMovement();
        this.getSitemapVersionLevelAsset = getSitemapVersionLevelAsset;
        this.googleMapsUrl = null; //
        this.getGoogleMapBackgroundApiKey = mapApiKeys.getGoogleMapBackgroundApiKey;
        this.osMapsUrl = `https://api.os.uk/maps/vector/v1/vts/resources/styles?srs=3857&key=${mapApiKeys.osMapsApiKey}`;
    }

    private createCadMapDraw() {
        const bboxDrawModes = MapboxDraw.modes;
        bboxDrawModes.drawRectangle = DrawRectangle;
        const draw = new MapboxDraw({
            modes: bboxDrawModes,
            displayControlsDefault: false,
        });
        return draw;
    }

    private createBackgroundMapDraw() {
        const draw = new MapboxDraw({
            displayControlsDefault: false,
        });
        return draw;
    }

    private createMapbackgroundMap(element: HTMLDivElement) {
        const map = new Map({
            container: element,
            center: [50, 50],
            zoom: 2,
            projection: "mercator",
            style: "mapbox://styles/mapbox/standard",
            maxPitch: 0,
            maxZoom: 24,
            bearingSnap: 0,
            logoPosition: "bottom-right",
            transformRequest: (url) => mapRequestInterceptor(url),
            pitchWithRotate: false,
            dragRotate: false,
            touchZoomRotate: false,
        });
        map.dragPan.disable();

        map.doubleClickZoom.disable();

        map.once("style.load", () => {
            map.addControl(this.backgroundMapDraw);
            this.setUpControlPointVisuals(map, "#00FF00", 10);
        });
        map.once("idle", () => {
            this.backgroundMapReady = true;
            if (this.cadMapReady) {
                this.onLoaded();
            }
            map.dragPan.enable({
                linearity: 1, // Normal (no easing in/out)
                maxSpeed: 10, // A low but non-zero speed
                deceleration: 10000, // High value to stop quickly
            });
        });
        map.on("moveend", () => {
            if (this.displayControlPoints) {
                this.updateControlPointsPosition();
            }
        });
        return map;
    }

    private createCadMap(element: HTMLDivElement) {
        const map = new Map({
            container: element,
            center: [0, 0],
            zoom: 2,
            projection: "mercator",
            maxPitch: 0,
            style: {
                layers: [],
                sources: {},
                version: 8,
                glyphs: "mapbox://fonts/joshnice/{fontstack}/{range}.pbf",
            },
            maxZoom: 24,
            bearingSnap: 0,
            pitchWithRotate: false,
            dragRotate: false,
            touchZoomRotate: false,
        });
        map.once("style.load", () => {
            map.addControl(this.cadMapDraw);
            this.setUpControlPointVisuals(map, "#0000FF", 5);
        });
        map.once("idle", () => {
            this.cadMapReady = true;
            if (this.backgroundMapReady) {
                this.onLoaded();
            }
        });
        map.doubleClickZoom.disable();
        return map;
    }

    private syncMouseEventsBetweenMaps() {
        const cadMapCanvasElement = this.cadMap.getCanvas();
        const mapBackgroundCanvasElement = this.backgroundMap.getCanvas();

        if (cadMapCanvasElement == null) {
            throw new Error("Canvas element can't be found for the Cad map");
        }

        cadMapCanvasElement.addEventListener("mousedown", (event) => {
            if (this.allowBackgroundMove) {
                mapBackgroundCanvasElement.dispatchEvent(new MouseEvent(event.type, event));
            }
        });

        cadMapCanvasElement.addEventListener("mouseup", (event) => {
            if (this.allowBackgroundMove) {
                mapBackgroundCanvasElement.dispatchEvent(new MouseEvent(event.type, event));
            }
        });

        cadMapCanvasElement.addEventListener("mousemove", (event) => {
            if (this.allowBackgroundMove) {
                mapBackgroundCanvasElement.dispatchEvent(new MouseEvent(event.type, event));
            }
        });
    }

    public onStartPerimeterDraw() {
        if (this.mode !== GeoReferencerCadMode.Perimeter) {
            return;
        }
        this.backgroundMapDraw.deleteAll();
        this.backgroundMapDraw.changeMode("draw_polygon");
    }

    private getControlPoints(): { corner: CornerPosition; realWorld: number[]; cadWorld: Position; cad: Position }[] {
        const cadCoords = this.cadControlPoints;
        if (cadCoords == null) {
            throw new Error("Cad coordinates are null");
        }
        const controlPoints = cadCoords.map((coord) => {
            const canvas = this.cadMap.project({ lng: coord.cadMapWorld[0], lat: coord.cadMapWorld[1] });
            const realWorld = this.backgroundMap.unproject([canvas.x, canvas.y]);

            return {
                corner: coord.corner,
                realWorld: [realWorld.lng, realWorld.lat],
                cadWorld: coord.cadMapWorld,
                cad: coord.cad,
            } as { corner: CornerPosition; realWorld: number[]; cadWorld: Position; cad: Position };
        });
        return controlPoints;
    }

    public startCadMapDraw() {
        this.cadMapDraw.deleteAll();
        this.cadMapDraw.changeMode("draw_polygon");
    }

    public startBackgroundMapDraw() {
        this.backgroundMapDraw.deleteAll();
        this.backgroundMapDraw.changeMode("draw_polygon");

        this.enableCadMapMovement();
        this.enableBackgroundMapMovement();

        // Disable map movement when editing the background map polygon
        this.backgroundMap.on("draw.selectionchange", (e: { features?: { id: string }[] }) => {
            if (e.features && e.features.length > 0) {
                this.disableCadMapMovement();
                this.disableBackgroundMapMovement();
            } else {
                this.enableCadMapMovement();
                this.enableBackgroundMapMovement();
            }
        });
    }

    public async project(units: CadUploadConfigUnit, rotation: number, fitToCad: boolean, layerIdsToExclude: string[], origin?: [number, number], cadClip?: number[][]) {
        // Cancel any ongoing projection polling
        if (this.projectionAbortController) {
            this.projectionAbortController.abort();
        }
        this.projectionAbortController = new AbortController();
        const { signal } = this.projectionAbortController;
        // clear any existing cad source
        const existingSource = this.cadMap.getSource(cadSourceId);
        if (existingSource) {
            // need to remove the layers first
            const cadMapStyle = this.cadMap.getStyle();
            if (cadMapStyle != null) {
                const fillLayer = cadMapStyle.layers.find((l) => l.id === cadFillLayer.id);
                const lineLayer = cadMapStyle.layers.find((l) => l.id === cadLineLayer.id);
                const textLayer = cadMapStyle.layers.find((l) => l.id === cadTextLayer.id);
                if (fillLayer != null) {
                    this.cadMap.removeLayer(cadFillLayer.id);
                }
                if (lineLayer != null) {
                    this.cadMap.removeLayer(cadLineLayer.id);
                }
                if (textLayer != null) {
                    this.cadMap.removeLayer(cadTextLayer.id);
                }
                // then remove the source
                this.cadMap.removeSource(cadSourceId);
            }
        }
        this.projecting$.next(true);
        try {
            // 1. PUT config to API
            const putConfig: PutCadApiConfig = {
                units,
                origin,
                rotation,
                cadClip,
            };
            await this.cadApiContext.updateConfig(putConfig);

            // 2. Poll every 500ms until projecting is false using a recursive async function
            this.projectionPercentage$.next(0);
            let config: CadApiConfig = null;
            const pollProjection = async (): Promise<void> => {
                if (signal.aborted) return;
                await new Promise((res) => setTimeout(res, 500));
                if (signal.aborted) return;
                try {
                    config = await this.cadApiContext.getConfig();
                    this.projectionPercentage$.next(config.projectionPercentage ?? 0);
                    if (config.state === "projecting") {
                        await pollProjection();
                    } else if (config.state === "idle") {
                        this.configRotation = config.rotation;
                        this.cadControlPoints = config.cadControlPoints.map((cp) => ({
                            corner: cp.cornerPosition,
                            cadMapWorld: [cp.transformedCad[0], cp.transformedCad[1]],
                            cad: cp.cad,
                        }));
                        this.originalCadBounds = config.originalCadBounds;
                        this.projectedCenter = config.origin;
                        this.units = config.units;
                    }
                } catch (e) {
                    if (signal.aborted) return;
                    await pollProjection();
                }
            };
            await pollProjection();

            if (signal.aborted) return;
            // 3. Add or update vector tile source
            const existingSource = this.cadMap.getSource(cadSourceId);
            const tileUrl = `${this.cadApiContext.apiUrl}tiles/{z}/{x}/{y}.pbf`;
            if (!existingSource) {
                this.cadMap.addSource(cadSourceId, {
                    type: "vector",
                    tiles: [tileUrl],
                    minzoom: 0,
                    maxzoom: 22,
                });
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } else if (typeof (existingSource as any).setTiles === "function") {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (existingSource as any).setTiles([tileUrl]);
            }
            if (fitToCad) {
                this.fitBackgroundToBounds([config.projectedBounds.maxX, config.projectedBounds.maxY, config.projectedBounds.minX, config.projectedBounds.minY]);
                this.fitCadToBounds([config.projectedBounds.maxX, config.projectedBounds.maxY, config.projectedBounds.minX, config.projectedBounds.minY]);
            } else {
                const backgroundBounds = this.backgroundMap.getBounds();
                this.cadMap.fitBounds(backgroundBounds, { duration: 0, padding: 0, animate: false });
            }
            const cadMapStyle = this.cadMap.getStyle();

            if (cadMapStyle == null) {
                throw new Error("Cad map style is null");
            }

            if (!cadMapStyle.layers.find((l) => l.source === cadSourceId)) {
                this.cadMap.addLayer(cadLineLayer);
                this.cadMap.addLayer(cadFillLayer);
                this.cadMap.addLayer(cadTextLayer);
                // Wait for style to load before filtering, and support aborting if projection is re-ran
                const waitForStyleAndFilter = () => {
                    if (!this.cadMap.isStyleLoaded()) {
                        const onStyleLoad = () => {
                            if (!signal.aborted) {
                                this.filterCadLayers(layerIdsToExclude);
                            }
                        };
                        this.cadMap.once("idle", onStyleLoad);
                    } else {
                        this.filterCadLayers(layerIdsToExclude);
                    }
                };
                waitForStyleAndFilter();
            }
            this.updateControlPointsPosition();
            this.projecting$.next(false);
        } catch (error) {
            this.projecting$.next(false);
            throw error;
        }
    }

    public showCad() {
        this.cadMap.setLayoutProperty(cadLineLayer.id, "visibility", "visible");
        this.cadMap.setLayoutProperty(cadFillLayer.id, "visibility", "visible");
        this.cadMap.setLayoutProperty(cadTextLayer.id, "visibility", "visible");
    }

    public hideCad() {
        const cadMapStyle = this.cadMap.getStyle();
        if (cadMapStyle == null) {
            throw new Error("Cad map style is null");
        }
        const fillLayer = cadMapStyle.layers.find((l) => l.id === cadFillLayer.id);
        const lineLayer = cadMapStyle.layers.find((l) => l.id === cadLineLayer.id);
        const textLayer = cadMapStyle.layers.find((l) => l.id === cadTextLayer.id);
        if (fillLayer != null) {
            this.cadMap.setLayoutProperty(cadFillLayer.id, "visibility", "none");
        }
        if (lineLayer != null) {
            this.cadMap.setLayoutProperty(cadLineLayer.id, "visibility", "none");
        }
        if (textLayer != null) {
            this.cadMap.setLayoutProperty(cadTextLayer.id, "visibility", "none");
        }
    }

    private async getGoogleMapsUrl() {
        if (this.googleMapsUrl != null) {
            return this.googleMapsUrl;
        }
        const keys = await this.getGoogleMapBackgroundApiKey();
        this.googleMapsUrl = `https://tile.googleapis.com/v1/2dtiles/{z}/{x}/{y}?session=${keys.session}&key=${keys.apiKey}`;
        return this.googleMapsUrl;
    }

    public async addSitemapVersionLevel(sitemapVersionLevelId: string) {
        if (this.existingSitemapVersionLevelOverlay == null || this.existingSitemapVersionLevelOverlay.id !== sitemapVersionLevelId) {
            const asset = await this.getSitemapVersionLevelAsset(sitemapVersionLevelId);
            const styleAssetUrl = `${asset.assetUrl}&${asset.authoritySignature}`;
            const { data } = await axios.get(styleAssetUrl);
            const tileUrl = `${asset.authorityUrl}tiles/{z}/{x}/{y}.pbf?assetVersion=${asset.updatedAt.toISOString()}&${asset.authoritySignature}`;
            // Get layers from style
            this.existingSitemapVersionLevelOverlay = {
                id: sitemapVersionLevelId,
                style: data,
                sourceId: data.layers[0].source,
                tileUrl,
            };
        }
        if (!this.backgroundMap.getSource(this.existingSitemapVersionLevelOverlay.sourceId)) {
            const { style, sourceId, tileUrl } = this.existingSitemapVersionLevelOverlay;
            const sourceDef: VectorSourceSpecification = {
                type: "vector",
                tiles: [tileUrl],
            };
            // Use a local type for styleSource with optional bounds property
            type StyleSourceWithBounds = typeof style.sources[string] & { bounds?: number[] };
            const styleSource = style.sources && (style.sources[sourceId] as StyleSourceWithBounds);
            if (styleSource && Array.isArray(styleSource.bounds) && styleSource.bounds.length === 4 && styleSource.bounds.every((n) => typeof n === "number")) {
                sourceDef.bounds = styleSource.bounds as [number, number, number, number];
            }
            this.backgroundMap.addSource(sourceId, sourceDef);
        }
        this.existingSitemapVersionLevelOverlay.style.layers.forEach((layer) => {
            if (!this.backgroundMap.getLayer(layer.id)) {
                this.backgroundMap.addLayer(layer);
            }
        });
    }

    public removeSitemapVersionLevel() {
        if (this.existingSitemapVersionLevelOverlay != null) {
            this.existingSitemapVersionLevelOverlay.style.layers.forEach((layer) => {
                if (this.backgroundMap.getLayer(layer.id)) {
                    this.backgroundMap.removeLayer(layer.id);
                }
            });
            this.backgroundMap.removeSource(this.existingSitemapVersionLevelOverlay.sourceId);
            this.existingSitemapVersionLevelOverlay = null;
        }
    }

    public changeBackground(selectedMapBackground: MapBackground) {
        if (blankMapBackgrounds.includes(selectedMapBackground.name.toLowerCase())) {
            this.backgroundMap.setStyle({
                version: 8,
                glyphs: "mapbox://fonts/mapbox/{fontstack}/{range}.pbf",
                sources: {},
                layers: [
                    {
                        id: "background",
                        type: "background",
                        paint: {
                            "background-color": "#ffffff",
                        },
                    },
                ],
            });
        } else {
            this.backgroundMap.setStyle(selectedMapBackground.url);
        }

        this.backgroundMap.once("idle", () => {
            this.backgroundMap.getStyle().layers.forEach((layer) => {
                // Hide or show all 3d areas in the map background
                if (layer != null && layer.type === "fill-extrusion") {
                    this.backgroundMap.setLayoutProperty(layer.id, "visibility", "none");
                }

                // Hide or show 2d areas in their place
                if (layer != null && layer.type === "fill") {
                    this.backgroundMap.setLayerZoomRange(layer.id, layer.minzoom ?? 0, 24);
                }
            });

            if (this.displayControlPoints) {
                if (this.existingSitemapVersionLevelOverlay != null) {
                    this.addSitemapVersionLevel(this.existingSitemapVersionLevelOverlay.id);
                }
                this.setUpControlPointVisuals(this.backgroundMap, "#00FF00", 10);
                this.showControlPoints();
                this.updateControlPointsPosition();
            }
        });
    }

    public fitBackgroundToBounds(bounds: [number, number, number, number]) {
        this.backgroundMap.fitBounds(bounds, { duration: 0, padding: 0, animate: false });
    }

    public fitCadToBounds(bounds: [number, number, number, number]) {
        this.cadMap.fitBounds(bounds, { duration: 0, padding: 0 });
        const zoom = this.cadMap.getZoom();

        if (zoom == null) {
            throw new Error();
        }
        this.zoom$.next({ source: "cad-map", value: zoom });
        this.backgroundMap.setZoom(zoom);
    }

    private subscribeToZoomEvents($zoom: SubjectContext["mapZoom$"]) {
        const subscription = $zoom.subscribe(({ value, source }) => {
            if (source === "toolbar") {
                this.cadMap.setZoom(value);
                this.backgroundMap.setZoom(value);
            }
        });

        this.backgroundMap.on("zoom", () => {
            const zoom = this.backgroundMap.getZoom();
            if (zoom != null) {
                $zoom.next({ value: zoom, source: "background-map" });
            }
        });

        this.subscriptions.push(subscription);
    }

    public removeCadMapDrawings() {
        this.cadMapDraw.deleteAll();
    }

    public enableCadMapMovement() {
        this.cadMap.dragPan.enable({
            linearity: 1, // Normal (no easing in/out)
            maxSpeed: 10, // A low but non-zero speed
            deceleration: 10000, // High value to stop quickly
        });
    }

    public enableBackgroundMapMovement() {
        this.backgroundMap.dragPan.enable({
            linearity: 1, // Normal (no easing in/out)
            maxSpeed: 10, // A low but non-zero speed
            deceleration: 10000, // High value to stop quickly
        });
    }

    public getBackgroundMapCenter() {
        return this.backgroundMap.getCenter();
    }

    public resizeMap() {
        this.backgroundMap.resize();
        this.cadMap.resize();
    }

    public showControlPoints() {
        this.displayControlPoints = true;
        this.cadMap.setLayoutProperty("control-point", "visibility", "visible");
        this.backgroundMap.setLayoutProperty("control-point", "visibility", "visible");
    }

    public hideControlPoints() {
        this.displayControlPoints = false;
        this.cadMap.setLayoutProperty("control-point", "visibility", "none");
        this.backgroundMap.setLayoutProperty("control-point", "visibility", "none");
    }

    private setUpControlPointVisuals(map: Map, colour: string, radius: number) {
        const emptyCollection = {
            type: "FeatureCollection",
            features: [],
        } as FeatureCollection;
        map.addSource("control-point", {
            type: "geojson",
            data: emptyCollection,
        });
        map.addLayer({
            id: "control-point",
            type: "circle",
            source: "control-point",
            paint: {
                "circle-radius": radius,
                "circle-color": colour,
                "circle-opacity": 0.6,
            },
            layout: {
                visibility: "none",
            },
        });
    }

    public updateControlPointsPosition() {
        const controlPoints = this.getControlPoints();
        this.onControlPointsChange.next(controlPoints);
        const cadControlPoints = {
            type: "FeatureCollection",
            features:
                controlPoints.map((point) => ({
                    type: "Feature",
                    properties: { layer: "control-point" },
                    geometry: {
                        type: "Point",
                        coordinates: [point.cadWorld[0], point.cadWorld[1]],
                    },
                })) ?? [],
        } as FeatureCollection;
        const cadMapSource = this.cadMap.getSource("control-point");
        if (cadMapSource == null) {
            throw new Error(`Source 'control-point' has not been added to the map yet.`);
        }
        if (cadMapSource.type === "geojson") {
            cadMapSource.setData(cadControlPoints);
        }

        const mapBackgroundControlPoints = {
            type: "FeatureCollection",
            features:
                controlPoints.map((point) => ({
                    type: "Feature",
                    properties: { layer: "control-point" },
                    geometry: {
                        type: "Point",
                        coordinates: [point.realWorld[0], point.realWorld[1]],
                    },
                })) ?? [],
        } as FeatureCollection;
        const mapBackgroundSource = this.backgroundMap.getSource("control-point");
        if (mapBackgroundSource == null) {
            throw new Error(`Source 'control-point' has not been added to the map yet.`);
        }
        if (mapBackgroundSource.type === "geojson") {
            mapBackgroundSource.setData(mapBackgroundControlPoints);
        }
    }

    public onCadRightClick(): Observable<{ cadCoords: number[]; backgroundCoords: number[] }> {
        return new Observable((observer) => {
            const handler = (event) => {
                // Return the coordinates of the cad map and background map
                const cadCoords = this.cadMap.unproject(event.point);
                const backgroundCoords = this.backgroundMap.unproject(event.point);
                observer.next({
                    cadCoords: [cadCoords.lng, cadCoords.lat],
                    backgroundCoords: [backgroundCoords.lng, backgroundCoords.lat],
                });
            };

            this.cadMap.on("contextmenu", handler);

            return () => {
                this.cadMap.off("contextmenu", handler);
            };
        });
    }

    public onCadClicked() {
        return new Observable((observer) => {
            const handler = (event) => {
                const features = this.cadMap.queryRenderedFeatures(event.point, {
                    layers: [cadLineLayer.id, cadFillLayer.id],
                });
                if (features.length === 0) {
                    return;
                }
                observer.next(features);
            };

            this.cadMap.on("click", handler);

            return () => {
                this.cadMap.off("click", handler);
            };
        });
    }

    public onBackgroundMapDrawEnd() {
        return new Observable((observer) => {
            const handler = (event) => {
                observer.next(event.features[0]);
            };

            this.backgroundMap.on("draw.create", handler);
            this.backgroundMap.on("draw.update", handler);

            return () => {
                this.backgroundMap.off("draw.create", handler);
                this.backgroundMap.off("draw.update", handler);
            };
        });
    }

    public onCadMapDrawEnd() {
        return new Observable((observer) => {
            const handler = (event) => {
                observer.next(event.features[0]);
            };

            this.cadMap.on("draw.create", handler);
            this.cadMap.on("draw.update", handler);

            return () => {
                this.cadMap.off("draw.create", handler);
                this.cadMap.off("draw.update", handler);
            };
        });
    }

    public filterCadLayers(layerIds: string[]) {
        if (!this.cadMap.isStyleLoaded()) {
            return;
        }
        this.cadMap.setFilter(cadFillLayer.id, ["all", ["!in", "eLayer", ...layerIds], ["==", "$type", "Polygon"]]);
        this.cadMap.setFilter(cadLineLayer.id, ["all", ["!in", "eLayer", ...layerIds], ["==", "$type", "LineString"]]);
        this.cadMap.setFilter(cadTextLayer.id, ["all", ["!in", "eLayer", ...layerIds], ["==", "$type", "Point"]]);
    }

    public disableCadMapMovement() {
        this.cadMap.dragPan.disable();
        this.cadMap.boxZoom.disable();
        this.cadMap.touchZoomRotate.disable();
        this.cadMap.dragRotate.disable();
        this.cadMap.keyboard.disable();
        this.cadMap.doubleClickZoom.disable();
        this.cadMap.scrollZoom.disable();
    }

    public disableBackgroundMapMovement() {
        this.backgroundMap.dragPan.disable();
        this.backgroundMap.boxZoom.disable();
        this.backgroundMap.touchZoomRotate.disable();
        this.backgroundMap.dragRotate.disable();
        this.backgroundMap.keyboard.disable();
        this.backgroundMap.doubleClickZoom.disable();
        this.backgroundMap.scrollZoom.disable();
    }

    public destroy() {
        this.subscriptions.forEach((subscription) => {
            subscription.unsubscribe();
        });

        // Remove the background map draw.selectionchange listener
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        this.backgroundMap.off("draw.selectionchange", () => {});
    }

    /**
     * Set the rotation of the CAD map in degrees (0-360).
     * @param degrees Rotation in degrees (clockwise, 0 is north)
     */
    public setCadMapRotation(degrees: number) {
        // Mapbox uses bearing for rotation, where 0 is north, positive is clockwise
        // Clamp degrees to [0, 360)
        const normalized = ((degrees % 360) + 360) % 360;
        this.cadMap.rotateTo(normalized, { duration: 0 });
    }

    public getCadRealWorldCenter(): [number, number] {
        const centerXY = this.cadMap.project([this.projectedCenter[0], this.projectedCenter[1]]);
        const center = this.backgroundMap.unproject(centerXY);
        return [center.lng, center.lat];
    }
}
